<template>
  <v-dialog v-model="dialog" max-width="480" persistent>
    <template #activator="{ on, attrs }">
      <v-tooltip bottom>
        <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
          <button
            class="action-pill pill-dark"
            v-bind="{ ...attrs, ...tooltipAttrs }"
            v-on="{ ...on, ...tooltipOn }"
          >
            <v-icon small>mdi-view-module-outline</v-icon>
            <span>Módulos</span>
          </button>
        </template>
        <span>Configurar módulos del proyecto</span>
      </v-tooltip>
    </template>

    <v-card class="project-config-modal" elevation="0">

      <!-- Header -->
      <div class="modal-header">
        <div class="header-left">
          <div class="header-icon-wrap">
            <v-icon color="white" size="20">mdi-view-module-outline</v-icon>
          </div>
          <div>
            <div class="modal-title">Configuración de Módulos</div>
            <div class="modal-subtitle">Proyecto: <b>{{ project.name }}</b></div>
          </div>
        </div>
        <v-btn icon small @click="close" class="close-btn">
          <v-icon color="rgba(255,255,255,0.7)" size="18">mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Body -->
      <div class="modal-body">

        <div class="section-label">
          <v-icon size="13" color="#2D4B95" class="mr-1">mdi-toggle-switch-outline</v-icon>
          Módulos disponibles
        </div>

        <div v-if="loading" class="loading-box">
          <v-progress-circular indeterminate color="#2D4B95" size="26" width="2" />
        </div>

        <div v-else>
          <div
            v-for="module in modules"
            :key="module.id"
            class="closure-toggle-banner module-row"
            :class="{ active: isEnabled(module.id) }"
          >
            <div class="toggle-info">
              <v-icon size="15" :color="isEnabled(module.id) ? '#2D4B95' : '#8896b3'" class="mr-2">
                {{ isEnabled(module.id) ? 'mdi-check-circle-outline' : 'mdi-circle-outline' }}
              </v-icon>
              <div>
                <div class="toggle-title">{{ module.name }}</div>
                <div v-if="module.description" class="toggle-desc">{{ module.description }}</div>
              </div>
            </div>
            <button
              class="toggle-switch"
              :class="{ on: isEnabled(module.id) }"
              @click="toggleModule(module.id)"
              type="button"
            >
              <span class="toggle-knob" />
            </button>
          </div>

          <div v-if="!modules.length" class="info-box">
            <v-icon size="14" color="#8896b3" class="mr-1">mdi-information-outline</v-icon>
            <span>No hay módulos disponibles.</span>
          </div>
        </div>

      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button class="btn-cancel" @click="close">Cancelar</button>
        <button
          class="btn-save"
          :disabled="loading || saving"
          @click="saveConfig"
        >
          <v-icon size="15" color="white" class="mr-1">mdi-content-save-outline</v-icon>
          Guardar configuración
        </button>
      </div>

    </v-card>
  </v-dialog>
</template>

<script>
import { setProjectModules, getSystemModules } from '@/helpers/api/admin'

export default {
  name: 'ProjectModulesConfigModal',

  props: {
    project: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      dialog: false,
      loading: false,
      saving: false,
      modules: [],
      enabledMap: {}
    }
  },

  watch: {
    dialog(val) {
      if (val) this.loadConfiguration()
    }
  },

  methods: {
    async loadConfiguration() {
      this.loading = true
      try {
        const system_modules = await getSystemModules()
				if(system_modules.code == 200){
					this.modules = system_modules.system_modules
				}

        const projectModules = this.project.system_modules || []
        const map = {}
        this.modules.forEach(module => {
          const existing = projectModules.find(pm => pm.id === module.id)
          map[module.id] = existing ? !!existing.pivot?.enabled : false
        })
        this.enabledMap = map
      } finally {
        this.loading = false
      }
    },

    isEnabled(moduleId) {
      return !!this.enabledMap[moduleId]
    },

    toggleModule(moduleId) {
      this.$set(this.enabledMap, moduleId, !this.enabledMap[moduleId])
    },

    async saveConfig() {
      this.saving = true
      try {
        const payload = {
          project_id: this.project.id,
          system_modules: this.modules.map(module => ({
            id: module.id,
            enabled: !!this.enabledMap[module.id]
          }))
        }
        await setProjectModules(payload)
        this.$emit('updated')
        this.close()
      } finally {
        this.saving = false
      }
    },

    close() {
      this.dialog = false
    }
  }
}
</script>

<style scoped>
* { box-sizing: border-box; }

.project-config-modal {
  border-radius: 16px !important;
  overflow: hidden;
  background: #ffffff;
  display: flex !important;
  flex-direction: column !important;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px 16px;
  background: linear-gradient(135deg, #2D4B95 0%, #1a2f63 100%);
}
.header-left { display: flex; align-items: center; gap: 12px; }
.header-icon-wrap {
  width: 36px; height: 36px; border-radius: 10px;
  background: rgba(255,255,255,0.15);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.modal-title { font-size: 14px; font-weight: 600; color: #fff; line-height: 1.2; }
.modal-subtitle { font-size: 11px; color: rgba(255,255,255,0.6); margin-top: 2px; }
.close-btn { background: rgba(255,255,255,0.1) !important; border-radius: 8px !important; }

.modal-body { padding: 20px 22px; max-height: 55vh; overflow-y: auto; }

.section-label {
  font-size: 11px; font-weight: 600; color: #2D4B95;
  text-transform: uppercase; letter-spacing: 0.07em;
  display: flex; align-items: center; margin-bottom: 10px;
}

.loading-box {
  display: flex; align-items: center; justify-content: center;
  padding: 30px 0;
}

.closure-toggle-banner {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 12px; border-radius: 9px;
  border: 1.5px solid #e2e8f4; background: #f7f9fc;
  margin-bottom: 6px; transition: all 0.2s ease;
}
.closure-toggle-banner.active { border-color: rgba(45,75,149,0.4); background: #eef2fb; }
.module-row:last-child { margin-bottom: 0; }
.toggle-info { display: flex; align-items: center; }
.toggle-title { font-size: 12.5px; font-weight: 600; color: #2d3748; line-height: 1.2; }
.toggle-desc { font-size: 10.5px; color: #8896b3; margin-top: 1px; }
.toggle-switch {
  flex-shrink: 0; width: 34px; height: 19px; border-radius: 10px;
  border: none; background: #d1d9e8; cursor: pointer;
  position: relative; transition: background 0.22s ease; padding: 0;
}
.toggle-switch.on { background: #2D4B95; }
.toggle-knob {
  position: absolute; top: 2.5px; left: 2.5px;
  width: 14px; height: 14px; border-radius: 50%;
  background: white; box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  transition: transform 0.22s cubic-bezier(.34,1.56,.64,1); display: block;
}
.toggle-switch.on .toggle-knob { transform: translateX(15px); }

.info-box {
  background: #f7f9fc; border-radius: 8px;
  padding: 10px 13px; border: 0.5px solid #e2e8f4;
  font-size: 12px; color: #8896b3; line-height: 1.55;
  display: flex; align-items: flex-start;
}

.modal-footer {
  display: flex; align-items: center; justify-content: flex-end;
  gap: 8px; padding: 13px 22px;
  border-top: 1px solid #e8edf5; background: #f7f9fc;
}
.btn-cancel {
  padding: 8px 16px; border-radius: 8px;
  border: 1.5px solid #d1d9e8; background: transparent;
  font-size: 13px; font-weight: 500; color: #5a6a8a; cursor: pointer;
}
.btn-cancel:hover { background: #eef2fb; border-color: #b0bedd; }
.btn-save {
  display: flex; align-items: center;
  padding: 8px 18px; border-radius: 8px; border: none;
  background: linear-gradient(135deg, #2D4B95, #1a2f63);
  font-size: 13px; font-weight: 600; color: white;
  cursor: pointer; box-shadow: 0 3px 10px rgba(45,75,149,0.3);
}
.btn-save:hover:not(:disabled) { box-shadow: 0 5px 14px rgba(45,75,149,0.4); }
.btn-save:disabled { opacity: 0.45; cursor: not-allowed; box-shadow: none; }

.pill-dark {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 5px 11px 5px 8px; border-radius: 99px;
  border: none; cursor: pointer; font-size: 11px; font-weight: 600;
  background: rgba(45,75,149,0.12); color: #2D4B95;
  transition: filter 0.15s, transform 0.15s; outline: none;
}
.pill-dark:hover { filter: brightness(1.08); transform: translateY(-1px); }
.pill-dark .v-icon { font-size: 14px !important; color: #2D4B95; }
</style>